import { Injectable } from '@angular/core';
import { WindowService } from './window.service';
import { TelemetryService } from './telemetry.service';

type ThankYouProvider = 'internal' | 'mao' | 'tandem' | 'upsell';
type ThankYouClickSurface = 'image' | 'description' | 'button' | 'listing';

interface ThankYouOfferClickContext {
  thankYouProvider: ThankYouProvider;
  clickTarget: string;
  destinationUrl?: string;
  lead?: any;
  extra?: Record<string, string>;
}

@Injectable({
  providedIn: 'root'
})
export class ThankYouTelemetryService {
  constructor(
    private telemetryService: TelemetryService,
    private windowService: WindowService
  ) {}

  trackMaoOfferClick(context: { clickSurface: ThankYouClickSurface; destinationUrl: string; lead?: any }): void {
    const metadata = this.getMaoUrlMetadata(context.destinationUrl);
    this.trackOfferClick({
      thankYouProvider: 'mao',
      clickTarget: 'mao_offer_click',
      destinationUrl: context.destinationUrl,
      lead: context.lead,
      extra: {
        clickSurface: context.clickSurface,
        offerBrand: metadata.offerBrand,
        offerTitle: metadata.offerTitle,
        sourceId: metadata.sourceId,
      },
    });
  }

  trackMaoInjectedOfferClick(event: MouseEvent, lead?: any): void {
    const clickedElement = event.target as HTMLElement | null;
    const listing = clickedElement?.closest('.max-rnt-listing');
    if (!clickedElement || !listing) {
      return;
    }

    const destinationUrl = this.resolveMaoOfferUrl(clickedElement, listing as HTMLElement);
    if (!destinationUrl) {
      return;
    }

    this.trackMaoOfferClick({
      clickSurface: this.resolveMaoClickSurface(clickedElement),
      destinationUrl,
      lead,
    });
  }

  trackTandemInjectedOfferClick(event: MouseEvent, lead?: any): void {
    const clickedElement = event.target as HTMLElement | null;
    const listing = clickedElement?.closest('.max-ad-listing') as HTMLElement | null;
    if (!clickedElement || !listing) {
      return;
    }

    const destinationUrl = this.resolveTandemOfferUrl(listing);
    if (!destinationUrl) {
      return;
    }

    this.trackOfferClick({
      thankYouProvider: 'tandem',
      clickTarget: 'tandem_offer_click',
      destinationUrl,
      lead,
      extra: {
        clickSurface: this.resolveTandemClickSurface(clickedElement),
        offerTitle: this.getText(listing, '.max-ad-description h1'),
        offerRank: this.getText(listing, '.max-ad-rank .ad-number'),
      },
    });
  }

  private trackOfferClick(context: ThankYouOfferClickContext): void {
    const urlParams = new URLSearchParams(this.windowService.locationSearch);
    this.telemetryService.trackEvent('thank_you_offer_clicked', {
      thankYouProvider: context.thankYouProvider,
      journeyStage: 'post_conversion_engagement',
      pageSequence: 'final',
      thanksSubId: context.lead?.ThanksSubID || urlParams.get('ThanksSubID') || '',
      campaign: context.lead?.Campaign || urlParams.get('Campaign') || '',
      subId: context.lead?.SubID || urlParams.get('SubID') || '',
      clickTarget: context.clickTarget,
      destinationUrl: context.destinationUrl || '',
      ...(context.extra || {}),
    });
  }

  private getMaoUrlMetadata(destinationUrl: string): { offerBrand: string; offerTitle: string; sourceId: string } {
    try {
      const parsedUrl = new URL(destinationUrl);
      return {
        offerBrand: parsedUrl.searchParams.get('Brand') || '',
        offerTitle: parsedUrl.searchParams.get('Title') || '',
        sourceId: parsedUrl.searchParams.get('SourceID') || '',
      };
    } catch {
      return {
        offerBrand: '',
        offerTitle: '',
        sourceId: '',
      };
    }
  }

  private resolveMaoOfferUrl(clickedElement: HTMLElement, listing: HTMLElement): string {
    const anchor = clickedElement.closest('a[href]') as HTMLAnchorElement | null;
    if (anchor?.href) {
      return anchor.href;
    }

    const onclickElement = this.findClosestOnclickElement(clickedElement, listing);
    const onclick = onclickElement?.getAttribute('onclick') || '';
    const match = onclick.match(/window\.open\(['"]([^'"]+)['"]/);
    return match?.[1] || '';
  }

  private findClosestOnclickElement(clickedElement: HTMLElement, listing: HTMLElement): HTMLElement | null {
    let element: HTMLElement | null = clickedElement;
    while (element && element !== listing.parentElement) {
      if (element.hasAttribute('onclick')) {
        return element;
      }
      element = element.parentElement;
    }
    return null;
  }

  private resolveMaoClickSurface(clickedElement: HTMLElement): ThankYouClickSurface {
    if (clickedElement.closest('.max-rnt-button, a.btn')) {
      return 'button';
    }

    if (clickedElement.closest('.max-rnt-description')) {
      return 'description';
    }

    return 'image';
  }

  private resolveTandemOfferUrl(listing: HTMLElement): string {
    const onclick = listing.getAttribute('onclick') || '';
    const match = onclick.match(/MediaAlphaExchange__click\(\s*\d+\s*,\s*['"]([^'"]+)['"]/);
    return this.normalizeUrl(match?.[1] || '');
  }

  private resolveTandemClickSurface(clickedElement: HTMLElement): ThankYouClickSurface {
    if (clickedElement.closest('.max-ad-button, .action-button')) {
      return 'button';
    }

    if (clickedElement.closest('.max-ad-description')) {
      return 'description';
    }

    if (clickedElement.closest('.max-ad-image')) {
      return 'image';
    }

    return 'listing';
  }

  private getText(parent: HTMLElement, selector: string): string {
    return parent.querySelector<HTMLElement>(selector)?.textContent?.trim() || '';
  }

  private normalizeUrl(url: string): string {
    if (url.startsWith('//')) {
      return `https:${url}`;
    }

    return url;
  }
}
